/**
 * @file src/config/constants.js
 * @description App-wide constants and public environment-derived values.
 * Rules:
 *  - No secrets here — Vite env values are public at build time
 *  - No React, no axios
 *  - Everything that appears as a magic string/number anywhere in the
 *    codebase should live here instead
 */

// ---------------------------------------------------------------------------
// Public origins
// ---------------------------------------------------------------------------

/**
 * Public marketing website origin.
 * This is not a secret. Vite env values are embedded into the frontend build.
 */
export const MARKETING_SITE_URL =
  import.meta.env.VITE_MARKETING_SITE_URL ?? "https://www.auditguardai.com";

/**
 * Public beta app origin.
 * This is also the public origin used for backend API access from the marketing site.
 */
export const BETA_APP_URL =
  import.meta.env.VITE_BETA_APP_URL ?? "https://beta.auditguardai.com";

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

/** Full versioned API prefix — used only in lib/axios.js and lib/sseMiddleware.js */
export const API_PREFIX = "/v1/api";

const currentHostname =
  typeof window !== "undefined" ? window.location.hostname : "";

const defaultApiBaseUrl = import.meta.env.DEV
  ? "http://localhost:4000"
  : currentHostname === "www.auditguardai.com"
    ? BETA_APP_URL
    : "";

const configuredApiBaseUrl = import.meta.env.VITE_API_URL;

/**
 * Base URL for all API calls.
 *
 * Behavior:
 *  - Local dev defaults to http://localhost:4000
 *  - beta.auditguardai.com uses same-origin API calls: /v1/api/*
 *  - www.auditguardai.com calls https://beta.auditguardai.com/v1/api/*
 *
 * The /v1/api prefix is appended in lib/axios.js, so callers only write
 * paths like "/auth/login".
 */
export const API_BASE_URL = (
  configuredApiBaseUrl && configuredApiBaseUrl.trim()
    ? configuredApiBaseUrl
    : defaultApiBaseUrl
).replace(/\/+$/, "");

// ---------------------------------------------------------------------------
// ZATCA — Seller / Supplier field rules (BR-KSA-* from XML Implementation Standard v1.2)
// ---------------------------------------------------------------------------

/** VAT number: exactly 15 digits, first and last digit must be 3 (BR-KSA-40) */
export const VAT_NUMBER_REGEX = /^3\d{13}3$/;

/** Building number: exactly 4 digits (BR-KSA-37) */
export const BUILDING_NUMBER_REGEX = /^\d{4}$/;

/** Postal code: exactly 5 digits (BR-KSA-66) */
export const POSTAL_CODE_REGEX = /^\d{5}$/;

/** Saudi phone number in E.164 format */
export const SAUDI_PHONE_REGEX = /^\+966[0-9]{9}$/;

/** Email — basic RFC-compliant check */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Country code fixed value for Saudi Arabia (BR-09) */
export const KSA_COUNTRY_CODE = "SA";

/**
 * ZATCA-approved Seller Identification Scheme IDs (BR-KSA-08).
 * Exactly one must be provided per invoice.
 */
export const SELLER_SCHEME_IDS = [
  { value: "CRN", label: "Commercial Registration Number (CRN)" },
  { value: "MOM", label: "MOMRAH License (MOM)" },
  { value: "MLS", label: "MHRSD License (MLS)" },
  { value: "700", label: "700 Number" },
  { value: "SAG", label: "MISA License (SAG)" },
  { value: "OTH", label: "Other (OTH)" },
];

/**
 * ZATCA VAT category codes (UN/CEFACT 5305, D.16B — section 11.2.4)
 */
export const VAT_CATEGORY_CODES = {
  STANDARD: "S", // Standard rate (15%)
  ZERO: "Z", // Zero rated
  EXEMPT: "E", // Exempt from VAT
  OUT_OF_SCOPE: "O", // Not subject to VAT
};

/**
 * ZATCA invoice type codes (UN/CEFACT 1001 — section 11.2.1)
 */
export const INVOICE_TYPE_CODES = {
  TAX_INVOICE: 388,
  PREPAYMENT: 386,
  DEBIT_NOTE: 383,
  CREDIT_NOTE: 381,
};

/**
 * ZATCA invoice subtypes (KSA-2, positions 1-2)
 */
export const INVOICE_SUBTYPES = {
  STANDARD: "01", // Tax Invoice (B2B / B2G)
  SIMPLIFIED: "02", // Simplified Tax Invoice (B2C)
};

// ---------------------------------------------------------------------------
// Query cache
// ---------------------------------------------------------------------------

/**
 * Default stale time for non-critical queries (10 minutes).
 * Override per-query in queryKeys.js where needed.
 */
export const DEFAULT_STALE_TIME = 1000 * 60 * 10;

/**
 * Default garbage collection time (30 minutes).
 */
export const DEFAULT_GC_TIME = 1000 * 60 * 30;

// ---------------------------------------------------------------------------
// SSE (Server-Sent Events)
// ---------------------------------------------------------------------------

/** SSE invoice processing events endpoint — path only, base URL prepended in sseMiddleware */
export const SSE_INVOICE_EVENTS_PATH =
  "/invoice-uploads/invoice-processing/events";

/** SSE reconnection — max attempts before giving up */
export const SSE_MAX_RECONNECT_ATTEMPTS = 5;

/** SSE reconnection — base delay in ms doubled each attempt */
export const SSE_RECONNECT_BASE_DELAY_MS = 2000;

// ---------------------------------------------------------------------------
// Invoice processing stages
// Defines the canonical order used in invoiceProgressSlice
// ---------------------------------------------------------------------------

export const INVOICE_STAGES = [
  "uploaded",
  "processed",
  "validated",
  "submitted",
];

// ---------------------------------------------------------------------------
// Local storage keys
// ---------------------------------------------------------------------------

export const STORAGE_KEYS = {
  COMPANY_ONBOARDING: "companyOnboardingProgress",
  REACT_QUERY_CACHE: "react-query-cache",
};

// ---------------------------------------------------------------------------
// Pagination defaults
// ---------------------------------------------------------------------------

export const DEFAULT_PAGE = 1;
export const DEFAULT_PAGE_SIZE = 10;

// ---------------------------------------------------------------------------
// File upload
// ---------------------------------------------------------------------------

/** Accepted MIME types for company logo upload */
export const LOGO_ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/svg+xml"];

/** Max logo file size in bytes (5 MB) */
export const LOGO_MAX_SIZE_BYTES = 5 * 1024 * 1024;
